import type { AreaPageConfig } from './areaPages.tsx';

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/'/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/--+/g, '-')
    .replace(/^-|-$/g, '');
}

const REMOVAL_IMAGES = [
  'https://images.pexels.com/photos/6169660/pexels-photo-6169660.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  'https://images.pexels.com/photos/5699438/pexels-photo-5699438.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  'https://images.pexels.com/photos/4498290/pexels-photo-4498290.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  'https://images.pexels.com/photos/5683286/pexels-photo-5683286.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
  'https://images.pexels.com/photos/6489172/pexels-photo-6489172.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
];

function pickImage(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return REMOVAL_IMAGES[hash % REMOVAL_IMAGES.length];
}

type RegionDef = {
  region: string;
  regionName: string;
  counties?: string;
  towns: Array<{ name: string; path: string }>;
  highlights: string[];
  movingSupport: string[];
  idealFor: string[];
  seoPoints: string[];
  faqs: Array<{ question: string; answer: string }>;
};

function generateIntro(town: string, regionName: string, counties?: string): string {
  const countyInfo = counties ? ` in ${counties}` : '';
  return `VIP House Movers provides professional home removals in ${town}${countyInfo}. Whether you are moving within ${town} or relocating to another part of ${regionName}, our team delivers careful handling, reliable transport, and a premium moving experience designed to reduce stress and keep your move on track.`;
}

function generateDescription(town: string, regionName: string): string {
  return `VIP House Movers offers dependable house removals in ${town}, ${regionName}. Professional home moving, packing support, and local removal services with fully insured care. Get a free quote today.`;
}

function generateConfigs(region: RegionDef): AreaPageConfig[] {
  return region.towns.map((town) => ({
    path: `/areas-we-cover/${town.path}`,
    navLabel: town.name,
    cardTitle: `${town.name} Removals`,
    title: `House Removals in ${town.name} | VIP House Movers`,
    description: generateDescription(town.name, region.regionName),
    intro: generateIntro(town.name, region.regionName, region.counties),
    highlights: region.highlights.map((h) => h.replace(/\{town\}/g, town.name)),
    movingSupport: region.movingSupport.map((s) => s.replace(/\{town\}/g, town.name)),
    idealFor: region.idealFor.map((i) => i.replace(/\{town\}/g, town.name)),
    seoPoints: region.seoPoints.map((p) => p.replace(/\{town\}/g, town.name)),
    faqs: region.faqs.map((f) => ({
      question: f.question.replace(/\{town\}/g, town.name),
      answer: f.answer.replace(/\{town\}/g, town.name),
    })),
    backgroundImage: pickImage(town.name),
  }));
}

const northWest: RegionDef = {
  region: 'north-west',
  regionName: 'North West England',
  counties: 'Greater Manchester, Lancashire, Merseyside, Cheshire, Cumbria',
  highlights: [
    'House removals, apartment moves, and office relocations across {town}',
    'Professional packing, loading, transport, and furniture assembly',
    'Experienced with terraced homes, flats, and larger family properties',
    'Flexible scheduling for local moves and long-distance relocations',
  ],
  movingSupport: [
    'Pre-move planning covering property access, parking, stairs, and timelines in {town}.',
    'Careful loading and secure transport for furniture, boxes, appliances, and fragile items.',
    'Support for same-day moves, pre-booked removals, and part-load transport.',
    'Clear communication from the first call until the last box is unloaded.',
  ],
  idealFor: [
    'Homeowners and tenants moving within {town}',
    'Families relocating to or from {town} and surrounding North West areas',
    'Businesses needing efficient office and commercial moving support',
  ],
  seoPoints: [
    'Perfect for customers searching for house removals in {town} and the North West.',
    'Ideal for local moves where parking, terraced access, and timing need planning.',
    'Suitable for families, professionals, and businesses needing reliable movers.',
    'A trusted option for fully insured removals with premium customer care.',
  ],
  faqs: [
    {
      question: 'Do you cover home removals in {town}?',
      answer: 'Yes. VIP House Movers supports house removals, flat moves, and business relocations in {town} and across North West England.',
    },
    {
      question: 'Can I add packing help for my {town} move?',
      answer: 'Yes. Packing services can be arranged alongside your removal to help protect and prepare your belongings.',
    },
  ],
  towns: [
    { name: 'Accrington', path: 'accrington' },
    { name: 'Alsager', path: 'alsager' },
    { name: 'Altrincham', path: 'altrincham' },
    { name: 'Ashton-under-Lyne', path: 'ashton-under-lyne' },
    { name: 'Bacup', path: 'bacup' },
    { name: 'Barrow-in-Furness', path: 'barrow-in-furness' },
    { name: 'Bebington', path: 'bebington' },
    { name: 'Birkenhead', path: 'birkenhead' },
    { name: 'Blackburn', path: 'blackburn' },
    { name: 'Blackpool', path: 'blackpool' },
    { name: 'Bolton', path: 'bolton' },
    { name: 'Bromborough', path: 'bromborough' },
    { name: 'Buckley', path: 'buckley' },
    { name: 'Burnley', path: 'burnley' },
    { name: 'Bury', path: 'bury' },
    { name: 'Carlisle', path: 'carlisle' },
    { name: 'Cheadle', path: 'cheadle' },
    { name: 'Cheadle Hulme', path: 'cheadle-hulme' },
    { name: 'Chester', path: 'chester' },
    { name: 'Chorley', path: 'chorley' },
    { name: 'Clitheroe', path: 'clitheroe' },
    { name: 'Colne', path: 'colne' },
    { name: 'Congleton', path: 'congleton' },
    { name: 'Crewe', path: 'crewe' },
    { name: 'Crosby', path: 'crosby' },
    { name: 'Darwen', path: 'darwen' },
    { name: 'Droylsden', path: 'droylsden' },
    { name: 'Eccles', path: 'eccles' },
    { name: 'Ellesmere Port', path: 'ellesmere-port' },
    { name: 'Farnworth', path: 'farnworth' },
    { name: 'Fleetwood', path: 'fleetwood' },
    { name: 'Formby', path: 'formby' },
    { name: 'Frodsham', path: 'frodsham' },
    { name: 'Great Harwood', path: 'great-harwood' },
    { name: 'Heywood', path: 'heywood' },
    { name: 'Holmes Chapel', path: 'holmes-chapel' },
    { name: 'Horwich', path: 'horwich' },
    { name: 'Hoylake', path: 'hoylake' },
    { name: 'Hyde', path: 'hyde' },
    { name: 'Kendal', path: 'kendal' },
    { name: 'Kirkby', path: 'kirkby' },
    { name: 'Knutsford', path: 'knutsford' },
    { name: 'Lancaster', path: 'lancaster' },
    { name: 'Leigh', path: 'leigh' },
    { name: 'Leyland', path: 'leyland' },
    { name: 'Liverpool', path: 'liverpool' },
    { name: 'Lymm', path: 'lymm' },
    { name: 'Lytham St Annes', path: 'lytham-st-annes' },
    { name: 'Macclesfield', path: 'macclesfield' },
    { name: 'Maghull', path: 'maghull' },
    { name: 'Manchester', path: 'manchester' },
    { name: 'Middleton', path: 'middleton' },
    { name: 'Middlewich', path: 'middlewich' },
    { name: 'Morecambe', path: 'morecambe' },
    { name: 'Nantwich', path: 'nantwich' },
    { name: 'Nelson', path: 'nelson' },
    { name: 'Neston', path: 'neston' },
    { name: 'New Ferry', path: 'new-ferry' },
    { name: 'Newton-le-Willows', path: 'newton-le-willows' },
    { name: 'Northwich', path: 'northwich' },
    { name: 'Oldham', path: 'oldham' },
    { name: 'Ormskirk', path: 'ormskirk' },
    { name: 'Oswaldtwistle', path: 'oswaldtwistle' },
    { name: 'Padiham', path: 'padiham' },
    { name: 'Penrith', path: 'penrith' },
    { name: 'Poulton-le-Fylde', path: 'poulton-le-fylde' },
    { name: 'Prescot', path: 'prescot' },
    { name: 'Preston', path: 'preston' },
    { name: 'Radcliffe', path: 'radcliffe' },
    { name: 'Rawtenstall', path: 'rawtenstall' },
    { name: 'Rochdale', path: 'rochdale' },
    { name: 'Romiley', path: 'romiley' },
    { name: 'Rossendale', path: 'rossendale' },
    { name: 'Royton', path: 'royton' },
    { name: 'Runcorn', path: 'runcorn' },
    { name: 'Sale', path: 'sale' },
    { name: 'Salford', path: 'salford' },
    { name: 'Sandbach', path: 'sandbach' },
    { name: 'Skelmersdale', path: 'skelmersdale' },
    { name: 'Southport', path: 'southport' },
    { name: 'St Helens', path: 'st-helens' },
    { name: 'Stalybridge', path: 'stalybridge' },
    { name: 'Stockport', path: 'stockport' },
    { name: 'Stretford', path: 'stretford' },
    { name: 'Swinton', path: 'swinton' },
    { name: 'Thornton-Cleveleys', path: 'thornton-cleveleys' },
    { name: 'Timperley', path: 'timperley' },
    { name: 'Urmston', path: 'urmston' },
    { name: 'Walkden', path: 'walkden' },
    { name: 'Wallasey', path: 'wallasey' },
    { name: 'Warrington', path: 'warrington' },
    { name: 'West Kirby', path: 'west-kirby' },
    { name: 'Westhoughton', path: 'westhoughton' },
    { name: 'Whitehaven', path: 'whitehaven' },
    { name: 'Widnes', path: 'widnes' },
    { name: 'Wigan', path: 'wigan' },
    { name: 'Wilmslow', path: 'wilmslow' },
    { name: 'Winsford', path: 'winsford' },
    { name: 'Workington', path: 'workington' },
    { name: 'Wythenshawe', path: 'wythenshawe' },
  ],
};

const northEast: RegionDef = {
  region: 'north-east',
  regionName: 'North East England',
  counties: 'Tyne and Wear, County Durham, Northumberland, Teesside',
  highlights: [
    'House removals and flat moves throughout {town}',
    'Office and business relocation support across the North East',
    'Packing services and furniture handling for all move sizes',
    'Reliable transport for local and long-distance removals',
  ],
  movingSupport: [
    'Move planning based on your property, access, and schedule in {town}.',
    'Safe loading, secure transport, and careful unloading for every item.',
    'Support for local moves, regional relocations, and cross-country moves.',
    'A dependable team focused on punctuality and customer care.',
  ],
  idealFor: [
    'Homeowners and tenants moving around {town}',
    'Families relocating within the North East',
    'Businesses needing efficient commercial moving help',
  ],
  seoPoints: [
    'Ideal for house removals in {town} and across North East England.',
    'Perfect for local moves where familiar routes and property knowledge matter.',
    'Suitable for families wanting packing, transport, and furniture support.',
    'A reliable option for customers seeking a fully insured removals team.',
  ],
  faqs: [
    {
      question: 'Do you offer home removals in {town}?',
      answer: 'Yes. VIP House Movers provides professional house removals in {town} and across the North East with fully insured service.',
    },
    {
      question: 'Can you help with office moves in {town}?',
      answer: 'Yes. We support commercial relocations for businesses in {town}, with careful planning and efficient transport.',
    },
  ],
  towns: [
    { name: 'Alnwick', path: 'alnwick' },
    { name: 'Ashington', path: 'ashington' },
    { name: 'Barnard Castle', path: 'barnard-castle' },
    { name: 'Berwick-upon-Tweed', path: 'berwick-upon-tweed' },
    { name: 'Billingham', path: 'billingham' },
    { name: 'Bishop Auckland', path: 'bishop-auckland' },
    { name: 'Blyth', path: 'blyth' },
    { name: 'Chester-le-Street', path: 'chester-le-street' },
    { name: 'Consett', path: 'consett' },
    { name: 'Cramlington', path: 'cramlington' },
    { name: 'Darlington', path: 'darlington' },
    { name: 'Durham', path: 'durham' },
    { name: 'Gateshead', path: 'gateshead' },
    { name: 'Hartlepool', path: 'hartlepool' },
    { name: 'Hexham', path: 'hexham' },
    { name: 'Jarrow', path: 'jarrow' },
    { name: 'Morpeth', path: 'morpeth' },
    { name: 'Newcastle upon Tyne', path: 'newcastle-upon-tyne' },
    { name: 'Newton Aycliffe', path: 'newton-aycliffe' },
    { name: 'North Shields', path: 'north-shields' },
    { name: 'Peterlee', path: 'peterlee' },
    { name: 'Seaham', path: 'seaham' },
    { name: 'South Shields', path: 'south-shields' },
    { name: 'Spennymoor', path: 'spennymoor' },
    { name: 'Stanley', path: 'stanley' },
    { name: 'Stockton-on-Tees', path: 'stockton-on-tees' },
    { name: 'Sunderland', path: 'sunderland' },
    { name: 'Wallsend', path: 'wallsend' },
    { name: 'Washington', path: 'washington' },
    { name: 'Whitley Bay', path: 'whitley-bay' },
  ],
};

const yorkshire: RegionDef = {
  region: 'yorkshire',
  regionName: 'Yorkshire and the Humber',
  counties: 'South Yorkshire, West Yorkshire, North Yorkshire, East Riding of Yorkshire',
  highlights: [
    'Comprehensive home removals across {town} and Yorkshire',
    'Support for flats, family homes, student moves, and offices',
    'Professional packing, furniture handling, and safe transport',
    'Experienced with both urban and rural Yorkshire properties',
  ],
  movingSupport: [
    'Planning around your {town} property, from terraced streets to village homes.',
    'Secure loading and transport for household belongings and business equipment.',
    'Flexible moving options for local Yorkshire moves and UK-wide relocations.',
    'A premium removals service built around communication and care.',
  ],
  idealFor: [
    'Families moving home in {town} and across Yorkshire',
    'Students and professionals relocating within the region',
    'Businesses seeking reliable office removal support',
  ],
  seoPoints: [
    'Designed for house removals in {town} and throughout Yorkshire.',
    'Suitable for terraced homes, student flats, family houses, and commercial moves.',
    'Helpful for customers searching for trusted movers with local knowledge.',
    'A strong choice for fully insured, professional removals with premium service.',
  ],
  faqs: [
    {
      question: 'Can you help with house removals in {town}?',
      answer: 'Yes. VIP House Movers covers home removals, flat moves, and business relocations in {town} and across Yorkshire.',
    },
    {
      question: 'Do you offer packing services in {town}?',
      answer: 'Yes. We can provide professional packing support alongside your removal service in {town}.',
    },
  ],
  towns: [
    { name: 'Acomb', path: 'acomb' },
    { name: 'Barnsley', path: 'barnsley' },
    { name: 'Batley', path: 'batley' },
    { name: 'Beverley', path: 'beverley' },
    { name: 'Bingley', path: 'bingley' },
    { name: 'Bradford', path: 'bradford' },
    { name: 'Bridlington', path: 'bridlington' },
    { name: 'Brighouse', path: 'brighouse' },
    { name: 'Castleford', path: 'castleford' },
    { name: 'Cleckheaton', path: 'cleckheaton' },
    { name: 'Dewsbury', path: 'dewsbury' },
    { name: 'Dinnington', path: 'dinnington' },
    { name: 'Doncaster', path: 'doncaster' },
    { name: 'Driffield', path: 'driffield' },
    { name: 'Elland', path: 'elland' },
    { name: 'Garforth', path: 'garforth' },
    { name: 'Goole', path: 'goole' },
    { name: 'Halifax', path: 'halifax' },
    { name: 'Harrogate', path: 'harrogate' },
    { name: 'Hebden Bridge', path: 'hebden-bridge' },
    { name: 'Heckmondwike', path: 'heckmondwike' },
    { name: 'Hessle', path: 'hessle' },
    { name: 'Holmfirth', path: 'holmfirth' },
    { name: 'Hornsea', path: 'hornsea' },
    { name: 'Huddersfield', path: 'huddersfield' },
    { name: 'Ilkley', path: 'ilkley' },
    { name: 'Keighley', path: 'keighley' },
    { name: 'Knaresborough', path: 'knaresborough' },
    { name: 'Leeds', path: 'leeds' },
    { name: 'Malton', path: 'malton' },
    { name: 'Mexborough', path: 'mexborough' },
    { name: 'Middlesbrough', path: 'middlesbrough' },
    { name: 'Mirfield', path: 'mirfield' },
    { name: 'Morley', path: 'morley' },
    { name: 'Northallerton', path: 'northallerton' },
    { name: 'Ossett', path: 'ossett' },
    { name: 'Otley', path: 'otley' },
    { name: 'Pickering', path: 'pickering' },
    { name: 'Pocklington', path: 'pocklington' },
    { name: 'Pontefract', path: 'pontefract' },
    { name: 'Pudsey', path: 'pudsey' },
    { name: 'Redcar', path: 'redcar' },
    { name: 'Ripon', path: 'ripon' },
    { name: 'Rotherham', path: 'rotherham' },
    { name: 'Scarborough', path: 'scarborough' },
    { name: 'Selby', path: 'selby' },
    { name: 'Sheffield', path: 'sheffield' },
    { name: 'Shipley', path: 'shipley' },
    { name: 'Skipton', path: 'skipton' },
    { name: 'Sowerby Bridge', path: 'sowerby-bridge' },
    { name: 'Thirsk', path: 'thirsk' },
    { name: 'Thornaby-on-Tees', path: 'thornaby-on-tees' },
    { name: 'Todmorden', path: 'todmorden' },
    { name: 'Wakefield', path: 'wakefield' },
    { name: 'Wath-upon-Dearne', path: 'wath-upon-dearne' },
    { name: 'Wetherby', path: 'wetherby' },
    { name: 'Whitby', path: 'whitby' },
    { name: 'Withernsea', path: 'withernsea' },
    { name: 'Yarm', path: 'yarm' },
    { name: 'Yeadon', path: 'yeadon' },
    { name: 'York', path: 'york' },
  ],
};

const eastMidlands: RegionDef = {
  region: 'east-midlands',
  regionName: 'East Midlands',
  counties: 'Derbyshire, Nottinghamshire, Leicestershire, Lincolnshire, Northamptonshire, Rutland',
  highlights: [
    'Professional house removals across {town} and the East Midlands',
    'Office relocations, packing services, and furniture handling',
    'Reliable transport for local and long-distance moves',
    'Fully insured service with a premium customer experience',
  ],
  movingSupport: [
    'Tailored planning for {town} homes, apartments, and commercial properties.',
    'Safe handling of furniture, fragile items, boxes, and business goods.',
    'Flexible support for urgent moves, planned relocations, and staged moves.',
    'Clear communication and dependable logistics from start to finish.',
  ],
  idealFor: [
    'Homeowners and tenants moving in {town}',
    'Families relocating across the East Midlands region',
    'Businesses needing professional office moving support',
  ],
  seoPoints: [
    'Ideal for house removals in {town} and the wider East Midlands.',
    'Perfect for customers who want packing, transport, and furniture support in one service.',
    'Works well for local moves, regional relocations, and longer-distance transport.',
    'A trusted option for fully insured removals with excellent customer care.',
  ],
  faqs: [
    {
      question: 'Do you cover house removals in {town}?',
      answer: 'Yes. VIP House Movers provides professional removals for homes and businesses in {town} and across the East Midlands.',
    },
    {
      question: 'Can I book a man and van service in {town}?',
      answer: 'Yes. We offer flexible man and van services in {town} for smaller moves and单项 item transport.',
    },
  ],
  towns: [
    { name: 'Alfreton', path: 'alfreton' },
    { name: 'Arnold', path: 'arnold' },
    { name: 'Ashby-de-la-Zouch', path: 'ashby-de-la-zouch' },
    { name: 'Bakewell', path: 'bakewell' },
    { name: 'Barton-upon-Humber', path: 'barton-upon-humber' },
    { name: 'Beeston', path: 'beeston' },
    { name: 'Belper', path: 'belper' },
    { name: 'Bingham', path: 'bingham' },
    { name: 'Bolsover', path: 'bolsover' },
    { name: 'Boston', path: 'boston' },
    { name: 'Bourne', path: 'bourne' },
    { name: 'Brackley', path: 'brackley' },
    { name: 'Buxton', path: 'buxton' },
    { name: 'Caistor', path: 'caistor' },
    { name: 'Carlton', path: 'carlton' },
    { name: 'Chesterfield', path: 'chesterfield' },
    { name: 'Cleethorpes', path: 'cleethorpes' },
    { name: 'Coalville', path: 'coalville' },
    { name: 'Corby', path: 'corby' },
    { name: 'Daventry', path: 'daventry' },
    { name: 'Derby', path: 'derby' },
    { name: 'Desborough', path: 'desborough' },
    { name: 'Dronfield', path: 'dronfield' },
    { name: 'Eastwood', path: 'eastwood' },
    { name: 'Gainsborough', path: 'gainsborough' },
    { name: 'Glossop', path: 'glossop' },
    { name: 'Grantham', path: 'grantham' },
    { name: 'Grimsby', path: 'grimsby' },
    { name: 'Heanor', path: 'heanor' },
    { name: 'Hinckley', path: 'hinckley' },
    { name: 'Hucknall', path: 'hucknall' },
    { name: 'Ilkeston', path: 'ilkeston' },
    { name: 'Immingham', path: 'immingham' },
    { name: 'Kettering', path: 'kettering' },
    { name: 'Kirkby-in-Ashfield', path: 'kirkby-in-ashfield' },
    { name: 'Leicester', path: 'leicester' },
    { name: 'Lincoln', path: 'lincoln' },
    { name: 'Long Eaton', path: 'long-eaton' },
    { name: 'Loughborough', path: 'loughborough' },
    { name: 'Louth', path: 'louth' },
    { name: 'Lutterworth', path: 'lutterworth' },
    { name: 'Mablethorpe', path: 'mablethorpe' },
    { name: 'Mansfield', path: 'mansfield' },
    { name: 'Market Harborough', path: 'market-harborough' },
    { name: 'Matlock', path: 'matlock' },
    { name: 'Melton Mowbray', path: 'melton-mowbray' },
    { name: 'Newark-on-Trent', path: 'newark-on-trent' },
    { name: 'North Hykeham', path: 'north-hykeham' },
    { name: 'Northampton', path: 'northampton' },
    { name: 'Nottingham', path: 'nottingham' },
    { name: 'Oadby', path: 'oadby' },
    { name: 'Raunds', path: 'raunds' },
    { name: 'Retford', path: 'retford' },
    { name: 'Ripley', path: 'ripley' },
    { name: 'Rushden', path: 'rushden' },
    { name: 'Scunthorpe', path: 'scunthorpe' },
    { name: 'Shepshed', path: 'shepshed' },
    { name: 'Skegness', path: 'skegness' },
    { name: 'Sleaford', path: 'sleaford' },
    { name: 'Spalding', path: 'spalding' },
    { name: 'Stamford', path: 'stamford' },
    { name: 'Sutton-in-Ashfield', path: 'sutton-in-ashfield' },
    { name: 'Swadlincote', path: 'swadlincote' },
    { name: 'Syston', path: 'syston' },
    { name: 'Towcester', path: 'towcester' },
    { name: 'Wellingborough', path: 'wellingborough' },
    { name: 'West Bridgford', path: 'west-bridgford' },
    { name: 'Wigston', path: 'wigston' },
    { name: 'Woodhall Spa', path: 'woodhall-spa' },
    { name: 'Worksop', path: 'worksop' },
  ],
};

const westMidlands: RegionDef = {
  region: 'west-midlands',
  regionName: 'West Midlands',
  counties: 'Staffordshire, Warwickshire, Worcestershire, Herefordshire, Shropshire',
  highlights: [
    'House removals, flat moves, and office relocations in {town}',
    'Professional packing, loading, transport, and furniture support',
    'Local knowledge of West Midlands roads, parking, and property access',
    'Flexible booking for planned moves and urgent relocations',
  ],
  movingSupport: [
    'Move planning tailored to your {town} property and schedule.',
    'Careful handling for all belongings from furniture to fragile items.',
    'Support for local and long-distance moves across the West Midlands and UK.',
    'A premium service focused on punctuality, care, and communication.',
  ],
  idealFor: [
    'Homeowners and tenants moving within {town}',
    'Families relocating across the West Midlands',
    'Businesses needing efficient commercial moving services',
  ],
  seoPoints: [
    'Designed for house removals in {town} and across the West Midlands.',
    'Ideal for local moves where parking, access, and timing need expert handling.',
    'Suitable for families, professionals, and businesses needing reliable movers.',
    'A trusted choice for fully insured removals with premium customer service.',
  ],
  faqs: [
    {
      question: 'Do you provide removals in {town}?',
      answer: 'Yes. VIP House Movers covers home removals, flat moves, and office relocations in {town} across the West Midlands.',
    },
    {
      question: 'Can you handle same-day removals in {town}?',
      answer: 'Yes, subject to availability. We can often support urgent moves in {town} when you need a team quickly.',
    },
  ],
  towns: [
    { name: 'Aldridge', path: 'aldridge' },
    { name: 'Atherstone', path: 'atherstone' },
    { name: 'Biddulph', path: 'biddulph' },
    { name: 'Birmingham', path: 'birmingham' },
    { name: 'Brierley Hill', path: 'brierley-hill' },
    { name: 'Bromsgrove', path: 'bromsgrove' },
    { name: 'Brownhills', path: 'brownhills' },
    { name: 'Burton upon Trent', path: 'burton-upon-trent' },
    { name: 'Cannock', path: 'cannock' },
    { name: 'Cheadle', path: 'cheadle-staffordshire' },
    { name: 'Coseley', path: 'coseley' },
    { name: 'Coventry', path: 'coventry' },
    { name: 'Cradley Heath', path: 'cradley-heath' },
    { name: 'Cwmbran', path: 'cwmbran' },
    { name: 'Droitwich Spa', path: 'droitwich-spa' },
    { name: 'Dudley', path: 'dudley' },
    { name: 'Erdington', path: 'erdington' },
    { name: 'Evesham', path: 'evesham' },
    { name: 'Halesowen', path: 'halesowen' },
    { name: 'Hednesford', path: 'hednesford' },
    { name: 'Hereford', path: 'hereford' },
    { name: 'Kenilworth', path: 'kenilworth' },
    { name: 'Kidderminster', path: 'kidderminster' },
    { name: 'Kidsgrove', path: 'kidsgrove' },
    { name: 'Leamington Spa', path: 'leamington-spa' },
    { name: 'Ledbury', path: 'ledbury' },
    { name: 'Leek', path: 'leek' },
    { name: 'Leominster', path: 'leominster' },
    { name: 'Lichfield', path: 'lichfield' },
    { name: 'Ludlow', path: 'ludlow' },
    { name: 'Malvern', path: 'malvern' },
    { name: 'Market Drayton', path: 'market-drayton' },
    { name: 'Newcastle-under-Lyme', path: 'newcastle-under-lyme' },
    { name: 'Newport', path: 'newport-shropshire' },
    { name: 'Nuneaton', path: 'nuneaton' },
    { name: 'Oldbury', path: 'oldbury' },
    { name: 'Oswestry', path: 'oswestry' },
    { name: 'Pershore', path: 'pershore' },
    { name: 'Redditch', path: 'redditch' },
    { name: 'Ross-on-Wye', path: 'ross-on-wye' },
    { name: 'Rowley Regis', path: 'rowley-regis' },
    { name: 'Rugby', path: 'rugby' },
    { name: 'Rugeley', path: 'rugeley' },
    { name: 'Shrewsbury', path: 'shrewsbury' },
    { name: 'Smethwick', path: 'smethwick' },
    { name: 'Solihull', path: 'solihull' },
    { name: 'Southam', path: 'southam' },
    { name: 'Stafford', path: 'stafford' },
    { name: 'Stoke-on-Trent', path: 'stoke-on-trent' },
    { name: 'Stone', path: 'stone' },
    { name: 'Stourbridge', path: 'stourbridge' },
    { name: 'Stourport-on-Severn', path: 'stourport-on-severn' },
    { name: 'Stratford-upon-Avon', path: 'stratford-upon-avon' },
    { name: 'Sutton Coldfield', path: 'sutton-coldfield' },
    { name: 'Tamworth', path: 'tamworth' },
    { name: 'Telford', path: 'telford' },
    { name: 'Tipton', path: 'tipton' },
    { name: 'Uttoxeter', path: 'uttoxeter' },
    { name: 'Walsall', path: 'walsall' },
    { name: 'Warwick', path: 'warwick' },
    { name: 'Wednesbury', path: 'wednesbury' },
    { name: 'West Bromwich', path: 'west-bromwich' },
    { name: 'Whitchurch', path: 'whitchurch' },
    { name: 'Willenhall', path: 'willenhall' },
    { name: 'Wolverhampton', path: 'wolverhampton' },
    { name: 'Worcester', path: 'worcester' },
  ],
};

const eastOfEngland: RegionDef = {
  region: 'east-of-england',
  regionName: 'East of England',
  counties: 'Cambridgeshire, Norfolk, Suffolk, Essex, Bedfordshire, Hertfordshire',
  highlights: [
    'Professional home removals across {town} and the East of England',
    'Office relocations with minimal downtime for businesses',
    'Expert packing services and careful furniture handling',
    'Reliable transport for local moves and nationwide relocations',
  ],
  movingSupport: [
    'Detailed planning around {town} property access, parking, and timelines.',
    'Careful loading, secure transport, and professional unloading.',
    'Support for family homes, student accommodation, and commercial moves.',
    'A premium service with clear communication throughout your move.',
  ],
  idealFor: [
    'Families moving home in {town} and the East of England',
    'Students relocating to and from university accommodation',
    'Businesses seeking reliable office and commercial removal services',
  ],
  seoPoints: [
    'Perfect for house removals in {town} and across the East of England.',
    'Ideal for customers wanting packing, transport, and furniture support from one team.',
    'Suitable for local moves, cross-region relocations, and long-distance transport.',
    'A trusted option for fully insured professional removals with premium care.',
  ],
  faqs: [
    {
      question: 'Do you cover house removals in {town}?',
      answer: 'Yes. VIP House Movers handles home removals, flat moves, and business relocations in {town} across the East of England.',
    },
    {
      question: 'Can I get packing help for my move in {town}?',
      answer: 'Yes. Professional packing services can be added to your removal booking in {town}.',
    },
  ],
  towns: [
    { name: 'Ampthill', path: 'ampthill' },
    { name: 'Aylsham', path: 'aylsham' },
    { name: 'Baldock', path: 'baldock' },
    { name: 'Barnet', path: 'barnet' },
    { name: 'Basildon', path: 'basildon' },
    { name: 'Beccles', path: 'beccles' },
    { name: 'Bedford', path: 'bedford' },
    { name: 'Berkhamsted', path: 'berkhamsted' },
    { name: 'Biggleswade', path: 'biggleswade' },
    { name: 'Billericay', path: 'billericay' },
    { name: "Bishop's Stortford", path: 'bishops-stortford' },
    { name: 'Borehamwood', path: 'borehamwood' },
    { name: 'Braintree', path: 'braintree' },
    { name: 'Brandon', path: 'brandon' },
    { name: 'Brentwood', path: 'brentwood' },
    { name: 'Broxbourne', path: 'broxbourne' },
    { name: 'Bungay', path: 'bungay' },
    { name: 'Bury St Edmunds', path: 'bury-st-edmunds' },
    { name: 'Cambridge', path: 'cambridge' },
    { name: 'Chatteris', path: 'chatteris' },
    { name: 'Chelmsford', path: 'chelmsford' },
    { name: 'Cheshunt', path: 'cheshunt' },
    { name: 'Chorleywood', path: 'chorleywood' },
    { name: 'Clacton-on-Sea', path: 'clacton-on-sea' },
    { name: 'Colchester', path: 'colchester' },
    { name: 'Cromer', path: 'cromer' },
    { name: 'Dereham', path: 'dereham' },
    { name: 'Diss', path: 'diss' },
    { name: 'Dunstable', path: 'dunstable' },
    { name: 'Ely', path: 'ely' },
    { name: 'Epping', path: 'epping' },
    { name: 'Fakenham', path: 'fakenham' },
    { name: 'Felixstowe', path: 'felixstowe' },
    { name: 'Flitwick', path: 'flitwick' },
    { name: 'Frinton-on-Sea', path: 'frinton-on-sea' },
    { name: 'Grays', path: 'grays' },
    { name: 'Great Yarmouth', path: 'great-yarmouth' },
    { name: 'Hadleigh', path: 'hadleigh' },
    { name: 'Harlow', path: 'harlow' },
    { name: 'Harpenden', path: 'harpenden' },
    { name: 'Harwich', path: 'harwich' },
    { name: 'Hatfield', path: 'hatfield' },
    { name: 'Haverhill', path: 'haverhill' },
    { name: 'Hemel Hempstead', path: 'hemel-hempstead' },
    { name: 'Hertford', path: 'hertford' },
    { name: 'Hitchin', path: 'hitchin' },
    { name: 'Hoddesdon', path: 'hoddesdon' },
    { name: 'Houghton Regis', path: 'houghton-regis' },
    { name: 'Hunstanton', path: 'hunstanton' },
    { name: 'Huntingdon', path: 'huntingdon' },
    { name: 'Ipswich', path: 'ipswich' },
    { name: 'Kesgrave', path: 'kesgrave' },
    { name: "King's Lynn", path: 'kings-lynn' },
    { name: 'Leighton Buzzard', path: 'leighton-buzzard' },
    { name: 'Letchworth', path: 'letchworth' },
    { name: 'Loughton', path: 'loughton' },
    { name: 'Lowestoft', path: 'lowestoft' },
    { name: 'Luton', path: 'luton' },
    { name: 'Maldon', path: 'maldon' },
    { name: 'March', path: 'march' },
    { name: 'Mildenhall', path: 'mildenhall' },
    { name: 'Newmarket', path: 'newmarket' },
    { name: 'North Walsham', path: 'north-walsham' },
    { name: 'Norwich', path: 'norwich' },
    { name: 'Peterborough', path: 'peterborough' },
    { name: 'Potters Bar', path: 'potters-bar' },
    { name: 'Ramsey', path: 'ramsey' },
    { name: 'Rayleigh', path: 'rayleigh' },
    { name: 'Rickmansworth', path: 'rickmansworth' },
    { name: 'Royston', path: 'royston' },
    { name: 'Saffron Walden', path: 'saffron-walden' },
    { name: 'Sandy', path: 'sandy' },
    { name: 'Sheringham', path: 'sheringham' },
    { name: 'Soham', path: 'soham' },
    { name: 'South Woodham Ferrers', path: 'south-woodham-ferrers' },
    { name: 'Southend-on-Sea', path: 'southend-on-sea' },
    { name: 'St Albans', path: 'st-albans' },
    { name: 'St Ives', path: 'st-ives-cambridgeshire' },
    { name: 'St Neots', path: 'st-neots' },
    { name: 'Stevenage', path: 'stevenage' },
    { name: 'Stowmarket', path: 'stowmarket' },
    { name: 'Sudbury', path: 'sudbury' },
    { name: 'Swaffham', path: 'swaffham' },
    { name: 'Thetford', path: 'thetford' },
    { name: 'Thurrock', path: 'thurrock' },
    { name: 'Tring', path: 'tring' },
    { name: 'Waltham Cross', path: 'waltham-cross' },
    { name: 'Walton-on-the-Naze', path: 'walton-on-the-naze' },
    { name: 'Ware', path: 'ware' },
    { name: 'Watford', path: 'watford' },
    { name: 'Wells-next-the-Sea', path: 'wells-next-the-sea' },
    { name: 'Welwyn Garden City', path: 'welwyn-garden-city' },
    { name: 'West Mersea', path: 'west-mersea' },
    { name: 'Wickford', path: 'wickford' },
    { name: 'Wisbech', path: 'wisbech' },
    { name: 'Witham', path: 'witham' },
    { name: 'Woodbridge', path: 'woodbridge' },
    { name: 'Wymondham', path: 'wymondham' },
    { name: 'Yaxley', path: 'yaxley' },
  ],
};

const southEast: RegionDef = {
  region: 'south-east',
  regionName: 'South East England',
  counties: 'Kent, Surrey, Sussex, Hampshire, Berkshire, Oxfordshire, Buckinghamshire, Isle of Wight',
  highlights: [
    'Complete home removal services across {town} and the South East',
    'Office relocations with structured planning and efficient transport',
    'Expert packing, furniture disassembly, and careful loading',
    'Experienced with city flats, suburban homes, and country properties',
  ],
  movingSupport: [
    'Thorough pre-move planning for {town} properties, from access to parking.',
    'Secure transport for household goods, business equipment, and fragile items.',
    'Flexible services for local moves, cross-county relocations, and UK-wide moves.',
    'Premium customer service with regular updates from quote to completion.',
  ],
  idealFor: [
    'Homeowners and tenants moving in {town}',
    'Families relocating across the South East region',
    'Businesses seeking efficient commercial moving support',
  ],
  seoPoints: [
    'Ideal for house removals in {town} and throughout South East England.',
    'Perfect for local moves in busy towns where access and parking need planning.',
    'Suitable for families wanting a full-service removal with packing and transport.',
    'A reliable choice for customers seeking fully insured professional movers.',
  ],
  faqs: [
    {
      question: 'Can you help with house removals in {town}?',
      answer: 'Yes. VIP House Movers provides professional removals for homes and businesses in {town} across the South East.',
    },
    {
      question: 'Do you offer storage solutions in {town}?',
      answer: 'Yes. We can arrange storage solutions alongside your removal service in {town} if needed.',
    },
  ],
  towns: [
    { name: 'Abingdon', path: 'abingdon' },
    { name: 'Addlestone', path: 'addlestone' },
    { name: 'Aldershot', path: 'aldershot' },
    { name: 'Alton', path: 'alton' },
    { name: 'Amersham', path: 'amersham' },
    { name: 'Andover', path: 'andover' },
    { name: 'Ascot', path: 'ascot' },
    { name: 'Ashford', path: 'ashford' },
    { name: 'Aylesbury', path: 'aylesbury' },
    { name: 'Banbury', path: 'banbury' },
    { name: 'Banstead', path: 'banstead' },
    { name: 'Basingstoke', path: 'basingstoke' },
    { name: 'Battle', path: 'battle' },
    { name: 'Beaconsfield', path: 'beaconsfield' },
    { name: 'Bexhill-on-Sea', path: 'bexhill-on-sea' },
    { name: 'Bicester', path: 'bicester' },
    { name: 'Bletchley', path: 'bletchley' },
    { name: 'Bognor Regis', path: 'bognor-regis' },
    { name: 'Bordon', path: 'bordon' },
    { name: 'Bracknell', path: 'bracknell' },
    { name: 'Brighton', path: 'brighton' },
    { name: 'Broadstairs', path: 'broadstairs' },
    { name: 'Buckingham', path: 'buckingham' },
    { name: 'Burford', path: 'burford' },
    { name: 'Burgess Hill', path: 'burgess-hill' },
    { name: 'Camberley', path: 'camberley' },
    { name: 'Canterbury', path: 'canterbury' },
    { name: 'Carterton', path: 'carterton' },
    { name: 'Caterham', path: 'caterham' },
    { name: 'Chatham', path: 'chatham' },
    { name: 'Chertsey', path: 'chertsey' },
    { name: 'Chesham', path: 'chesham' },
    { name: 'Chichester', path: 'chichester' },
    { name: 'Chipping Norton', path: 'chipping-norton' },
    { name: 'Cowes', path: 'cowes' },
    { name: 'Crawley', path: 'crawley' },
    { name: 'Crowborough', path: 'crowborough' },
    { name: 'Crowthorne', path: 'crowthorne' },
    { name: 'Dartford', path: 'dartford' },
    { name: 'Deal', path: 'deal' },
    { name: 'Didcot', path: 'didcot' },
    { name: 'Dorking', path: 'dorking' },
    { name: 'Dover', path: 'dover' },
    { name: 'East Cowes', path: 'east-cowes' },
    { name: 'East Grinstead', path: 'east-grinstead' },
    { name: 'Eastbourne', path: 'eastbourne' },
    { name: 'Eastleigh', path: 'eastleigh' },
    { name: 'Egham', path: 'egham' },
    { name: 'Epsom', path: 'epsom' },
    { name: 'Esher', path: 'esher' },
    { name: 'Fareham', path: 'fareham' },
    { name: 'Farnborough', path: 'farnborough' },
    { name: 'Farnham', path: 'farnham' },
    { name: 'Faversham', path: 'faversham' },
    { name: 'Fleet', path: 'fleet' },
    { name: 'Folkestone', path: 'folkestone' },
    { name: 'Fordingbridge', path: 'fordingbridge' },
    { name: 'Frimley', path: 'frimley' },
    { name: 'Gerrards Cross', path: 'gerrards-cross' },
    { name: 'Gillingham', path: 'gillingham' },
    { name: 'Godalming', path: 'godalming' },
    { name: 'Gosport', path: 'gosport' },
    { name: 'Gravesend', path: 'gravesend' },
    { name: 'Guildford', path: 'guildford' },
    { name: 'Hailsham', path: 'hailsham' },
    { name: 'Haslemere', path: 'haslemere' },
    { name: 'Hastings', path: 'hastings' },
    { name: 'Havant', path: 'havant' },
    { name: 'Haywards Heath', path: 'haywards-heath' },
    { name: 'Heathfield', path: 'heathfield' },
    { name: 'Henley-on-Thames', path: 'henley-on-thames' },
    { name: 'Herne Bay', path: 'herne-bay' },
    { name: 'High Wycombe', path: 'high-wycombe' },
    { name: 'Horley', path: 'horley' },
    { name: 'Horsham', path: 'horsham' },
    { name: 'Kidlington', path: 'kidlington' },
    { name: 'Lancing', path: 'lancing' },
    { name: 'Leatherhead', path: 'leatherhead' },
    { name: 'Lewes', path: 'lewes' },
    { name: 'Littlehampton', path: 'littlehampton' },
    { name: 'Lymington', path: 'lymington' },
    { name: 'Maidenhead', path: 'maidenhead' },
    { name: 'Maidstone', path: 'maidstone' },
    { name: 'Margate', path: 'margate' },
    { name: 'Marlow', path: 'marlow' },
    { name: 'Midhurst', path: 'midhurst' },
    { name: 'Milton Keynes', path: 'milton-keynes' },
    { name: 'New Milton', path: 'new-milton' },
    { name: 'New Romney', path: 'new-romney' },
    { name: 'Newbury', path: 'newbury' },
    { name: 'Newhaven', path: 'newhaven' },
    { name: 'Newport', path: 'newport' },
    { name: 'Newport Pagnell', path: 'newport-pagnell' },
    { name: 'Olney', path: 'olney' },
    { name: 'Oxford', path: 'oxford' },
    { name: 'Oxted', path: 'oxted' },
    { name: 'Peacehaven', path: 'peacehaven' },
    { name: 'Petersfield', path: 'petersfield' },
    { name: 'Portsmouth', path: 'portsmouth' },
    { name: 'Princes Risborough', path: 'princes-risborough' },
    { name: 'Rainham', path: 'rainham' },
    { name: 'Ramsgate', path: 'ramsgate' },
    { name: 'Reading', path: 'reading' },
    { name: 'Redhill', path: 'redhill' },
    { name: 'Reigate', path: 'reigate' },
    { name: 'Ringwood', path: 'ringwood' },
    { name: 'Rochester', path: 'rochester' },
    { name: 'Romsey', path: 'romsey' },
    { name: 'Rustington', path: 'rustington' },
    { name: 'Ryde', path: 'ryde' },
    { name: 'Rye', path: 'rye' },
    { name: 'Sandhurst', path: 'sandhurst' },
    { name: 'Sandown', path: 'sandown' },
    { name: 'Seaford', path: 'seaford' },
    { name: 'Selsey', path: 'selsey' },
    { name: 'Sevenoaks', path: 'sevenoaks' },
    { name: 'Shanklin', path: 'shanklin' },
    { name: 'Sheerness', path: 'sheerness' },
    { name: 'Shoreham-by-Sea', path: 'shoreham-by-sea' },
    { name: 'Sittingbourne', path: 'sittingbourne' },
    { name: 'Slough', path: 'slough' },
    { name: 'Southampton', path: 'southampton' },
    { name: 'Southwick', path: 'southwick' },
    { name: 'Staines-upon-Thames', path: 'staines-upon-thames' },
    { name: 'Steyning', path: 'steyning' },
    { name: 'Stony Stratford', path: 'stony-stratford' },
    { name: 'Sunbury-on-Thames', path: 'sunbury-on-thames' },
    { name: 'Swanley', path: 'swanley' },
    { name: 'Tadley', path: 'tadley' },
    { name: 'Tenterden', path: 'tenterden' },
    { name: 'Thame', path: 'thame' },
    { name: 'Tonbridge', path: 'tonbridge' },
    { name: 'Tunbridge Wells', path: 'tunbridge-wells' },
    { name: 'Twyford', path: 'twyford' },
    { name: 'Uckfield', path: 'uckfield' },
    { name: 'Ventnor', path: 'ventnor' },
    { name: 'Wallingford', path: 'wallingford' },
    { name: 'Walton-on-Thames', path: 'walton-on-thames' },
    { name: 'Wantage', path: 'wantage' },
    { name: 'Waterlooville', path: 'waterlooville' },
    { name: 'West Malling', path: 'west-malling' },
    { name: 'Weybridge', path: 'weybridge' },
    { name: 'Whitstable', path: 'whitstable' },
    { name: 'Winchester', path: 'winchester' },
    { name: 'Windsor', path: 'windsor' },
    { name: 'Winslow', path: 'winslow' },
    { name: 'Witney', path: 'witney' },
    { name: 'Woking', path: 'woking' },
    { name: 'Wokingham', path: 'wokingham' },
    { name: 'Woodstock', path: 'woodstock' },
    { name: 'Worthing', path: 'worthing' },
  ],
};

const southWest: RegionDef = {
  region: 'south-west',
  regionName: 'South West England',
  counties: 'Devon, Cornwall, Somerset, Dorset, Wiltshire, Gloucestershire, Bristol, Bath',
  highlights: [
    'Professional house removals across {town} and the South West',
    'Support for rural properties, city homes, and coastal relocations',
    'Packing services, furniture handling, and secure transport',
    'Experienced with narrow lanes, tight access, and longer travel distances',
  ],
  movingSupport: [
    'Pre-move planning for {town} properties including access and parking considerations.',
    'Safe loading and transport for household belongings and business equipment.',
    'Support for local moves, rural relocations, and cross-country journeys.',
    'Reliable service with clear communication and a premium customer focus.',
  ],
  idealFor: [
    'Homeowners and tenants moving in {town}',
    'Families relocating across the South West region',
    'Businesses needing professional moving support',
  ],
  seoPoints: [
    'Designed for house removals in {town} and across South West England.',
    'Ideal for coastal, rural, and city moves with local access expertise.',
    'Suitable for families wanting comprehensive packing and transport support.',
    'A trusted choice for fully insured removals with premium customer care.',
  ],
  faqs: [
    {
      question: 'Do you offer removals in {town}?',
      answer: 'Yes. VIP House Movers provides professional home and business removals in {town} across the South West.',
    },
    {
      question: 'Can you handle long-distance moves from {town}?',
      answer: 'Yes. We support long-distance relocations from {town} to destinations across the UK.',
    },
  ],
  towns: [
    { name: 'Amesbury', path: 'amesbury' },
    { name: 'Barnstaple', path: 'barnstaple' },
    { name: 'Bath', path: 'bath' },
    { name: 'Beaminster', path: 'beaminster' },
    { name: 'Bideford', path: 'bideford' },
    { name: "Bishop's Cleeve", path: 'bishops-cleeve' },
    { name: 'Blandford Forum', path: 'blandford-forum' },
    { name: 'Bodmin', path: 'bodmin' },
    { name: 'Bournemouth', path: 'bournemouth' },
    { name: 'Bradford-on-Avon', path: 'bradford-on-avon' },
    { name: 'Bridgwater', path: 'bridgwater' },
    { name: 'Bridport', path: 'bridport' },
    { name: 'Bristol', path: 'bristol' },
    { name: 'Brixham', path: 'brixham' },
    { name: 'Bude', path: 'bude' },
    { name: 'Burnham-on-Sea', path: 'burnham-on-sea' },
    { name: 'Calne', path: 'calne' },
    { name: 'Camborne', path: 'camborne' },
    { name: 'Chard', path: 'chard' },
    { name: 'Cheltenham', path: 'cheltenham' },
    { name: 'Chippenham', path: 'chippenham' },
    { name: 'Christchurch', path: 'christchurch' },
    { name: 'Cirencester', path: 'cirencester' },
    { name: 'Clevedon', path: 'clevedon' },
    { name: 'Coleford', path: 'coleford' },
    { name: 'Corsham', path: 'corsham' },
    { name: 'Crediton', path: 'crediton' },
    { name: 'Crewkerne', path: 'crewkerne' },
    { name: 'Dawlish', path: 'dawlish' },
    { name: 'Devizes', path: 'devizes' },
    { name: 'Dorchester', path: 'dorchester' },
    { name: 'Dursley', path: 'dursley' },
    { name: 'Exeter', path: 'exeter' },
    { name: 'Exmouth', path: 'exmouth' },
    { name: 'Falmouth', path: 'falmouth' },
    { name: 'Ferndown', path: 'ferndown' },
    { name: 'Fowey', path: 'fowey' },
    { name: 'Frome', path: 'frome' },
    { name: 'Glastonbury', path: 'glastonbury' },
    { name: 'Gloucester', path: 'gloucester' },
    { name: 'Hayle', path: 'hayle' },
    { name: 'Helston', path: 'helston' },
    { name: 'Highbridge', path: 'highbridge' },
    { name: 'Honiton', path: 'honiton' },
    { name: 'Ilfracombe', path: 'ilfracombe' },
    { name: 'Ivybridge', path: 'ivybridge' },
    { name: 'Keynsham', path: 'keynsham' },
    { name: 'Launceston', path: 'launceston' },
    { name: 'Liskeard', path: 'liskeard' },
    { name: 'Lostwithiel', path: 'lostwithiel' },
    { name: 'Lydney', path: 'lydney' },
    { name: 'Lyme Regis', path: 'lyme-regis' },
    { name: 'Marlborough', path: 'marlborough' },
    { name: 'Melksham', path: 'melksham' },
    { name: 'Midsomer Norton', path: 'midsomer-norton' },
    { name: 'Minehead', path: 'minehead' },
    { name: 'Nailsea', path: 'nailsea' },
    { name: 'Newent', path: 'newent' },
    { name: 'Newquay', path: 'newquay' },
    { name: 'Newton Abbot', path: 'newton-abbot' },
    { name: 'Okehampton', path: 'okehampton' },
    { name: 'Paignton', path: 'paignton' },
    { name: 'Penryn', path: 'penryn' },
    { name: 'Penzance', path: 'penzance' },
    { name: 'Plymouth', path: 'plymouth' },
    { name: 'Poole', path: 'poole' },
    { name: 'Portishead', path: 'portishead' },
    { name: 'Portland', path: 'portland' },
    { name: 'Redruth', path: 'redruth' },
    { name: 'Salisbury', path: 'salisbury' },
    { name: 'Saltash', path: 'saltash' },
    { name: 'Shaftesbury', path: 'shaftesbury' },
    { name: 'Shepton Mallet', path: 'shepton-mallet' },
    { name: 'Sherborne', path: 'sherborne' },
    { name: 'Sidmouth', path: 'sidmouth' },
    { name: 'St Austell', path: 'st-austell' },
    { name: 'St Ives', path: 'st-ives' },
    { name: 'Stow-on-the-Wold', path: 'stow-on-the-wold' },
    { name: 'Street', path: 'street' },
    { name: 'Stroud', path: 'stroud' },
    { name: 'Swanage', path: 'swanage' },
    { name: 'Swindon', path: 'swindon' },
    { name: 'Taunton', path: 'taunton' },
    { name: 'Tavistock', path: 'tavistock' },
    { name: 'Teignmouth', path: 'teignmouth' },
    { name: 'Tetbury', path: 'tetbury' },
    { name: 'Tewkesbury', path: 'tewkesbury' },
    { name: 'Tidworth', path: 'tidworth' },
    { name: 'Tiverton', path: 'tiverton' },
    { name: 'Torpoint', path: 'torpoint' },
    { name: 'Torquay', path: 'torquay' },
    { name: 'Totnes', path: 'totnes' },
    { name: 'Trowbridge', path: 'trowbridge' },
    { name: 'Truro', path: 'truro' },
    { name: 'Verwood', path: 'verwood' },
    { name: 'Wadebridge', path: 'wadebridge' },
    { name: 'Wareham', path: 'wareham' },
    { name: 'Warminster', path: 'warminster' },
    { name: 'Wells', path: 'wells' },
    { name: 'Westbury', path: 'westbury' },
    { name: 'Weston-super-Mare', path: 'weston-super-mare' },
    { name: 'Weymouth', path: 'weymouth' },
    { name: 'Wimborne Minster', path: 'wimborne-minster' },
    { name: 'Wootton Bassett', path: 'wootton-bassett' },
    { name: 'Yate', path: 'yate' },
    { name: 'Yeovil', path: 'yeovil' },
  ],
};

const greaterLondon: RegionDef = {
  region: 'greater-london',
  regionName: 'Greater London',
  counties: 'Greater London',
  highlights: [
    'House removals and flat moves across {town} and Greater London',
    'Expertise with apartments, concierge buildings, and restricted parking zones',
    'Professional packing, furniture handling, and secure transport',
    'Planning around London congestion, loading bays, and timed access',
  ],
  movingSupport: [
    'Detailed move planning for {town} including parking permits and building access.',
    'Careful handling for furniture, fragile items, and high-value possessions.',
    'Support for studio flats, family homes, and office relocations in London.',
    'A premium service designed for the demands of London moving.',
  ],
  idealFor: [
    'Homeowners and tenants moving within {town}',
    'Apartment and flat residents with access restrictions',
    'Businesses relocating offices across Greater London',
  ],
  seoPoints: [
    'Perfect for house removals in {town} and across Greater London.',
    'Ideal for London moves where parking, concierge, and timed access matter.',
    'Suitable for flats, apartments, family homes, and commercial relocations.',
    'A trusted option for fully insured removals with London-specific expertise.',
  ],
  faqs: [
    {
      question: 'Can you help with removals in {town}?',
      answer: 'Yes. VIP House Movers provides professional home and business removals in {town} across Greater London.',
    },
    {
      question: 'Do you handle apartment moves in {town}?',
      answer: 'Yes. We specialise in apartment and flat removals in {town}, including concierge buildings and restricted access properties.',
    },
  ],
  towns: [
    { name: 'Barking', path: 'barking' },
    { name: 'Barnes', path: 'barnes' },
    { name: 'Beckenham', path: 'beckenham' },
    { name: 'Bexleyheath', path: 'bexleyheath' },
    { name: 'Brent Cross', path: 'brent-cross' },
    { name: 'Bromley', path: 'bromley' },
    { name: 'Buckhurst Hill', path: 'buckhurst-hill' },
    { name: 'Carshalton', path: 'carshalton' },
    { name: 'Catford', path: 'catford' },
    { name: 'Chislehurst', path: 'chislehurst' },
    { name: 'Coulsdon', path: 'coulsdon' },
    { name: 'Croydon', path: 'croydon' },
    { name: 'Dagenham', path: 'dagenham' },
    { name: 'East Ham', path: 'east-ham' },
    { name: 'Edgware', path: 'edgware' },
    { name: 'Eltham', path: 'eltham' },
    { name: 'Enfield', path: 'enfield' },
    { name: 'Erith', path: 'erith' },
    { name: 'Feltham', path: 'feltham' },
    { name: 'Finchley', path: 'finchley' },
    { name: 'Golders Green', path: 'golders-green' },
    { name: 'Greenwich', path: 'greenwich' },
    { name: 'Hackney', path: 'hackney' },
    { name: 'Hammersmith', path: 'hammersmith' },
    { name: 'Hampstead', path: 'hampstead' },
    { name: 'Hampton', path: 'hampton' },
    { name: 'Hanwell', path: 'hanwell' },
    { name: 'Harrow', path: 'harrow' },
    { name: 'Hayes', path: 'hayes' },
    { name: 'Hendon', path: 'hendon' },
    { name: 'Hornchurch', path: 'hornchurch' },
    { name: 'Hounslow', path: 'hounslow' },
    { name: 'Ilford', path: 'ilford' },
    { name: 'Isleworth', path: 'isleworth' },
    { name: 'Kensington', path: 'kensington' },
    { name: 'Kingston upon Thames', path: 'kingston-upon-thames' },
    { name: 'Lambeth', path: 'lambeth' },
    { name: 'Lewisham', path: 'lewisham' },
    { name: 'Leyton', path: 'leyton' },
    { name: 'Mitcham', path: 'mitcham' },
    { name: 'Morden', path: 'morden' },
    { name: 'Northolt', path: 'northolt' },
    { name: 'Northwood', path: 'northwood' },
    { name: 'Orpington', path: 'orpington' },
    { name: 'Penge', path: 'penge' },
    { name: 'Pinner', path: 'pinner' },
    { name: 'Poplar', path: 'poplar' },
    { name: 'Purley', path: 'purley' },
    { name: 'Richmond', path: 'richmond' },
    { name: 'Romford', path: 'romford' },
    { name: 'Ruislip', path: 'ruislip' },
    { name: 'Sidcup', path: 'sidcup' },
    { name: 'South Norwood', path: 'south-norwood' },
    { name: 'Southall', path: 'southall' },
    { name: 'Southgate', path: 'southgate' },
    { name: 'Stanmore', path: 'stanmore' },
    { name: 'Stratford', path: 'stratford' },
    { name: 'Surbiton', path: 'surbiton' },
    { name: 'Sutton', path: 'sutton' },
    { name: 'Teddington', path: 'teddington' },
    { name: 'Tolworth', path: 'tolworth' },
    { name: 'Tottenham', path: 'tottenham' },
    { name: 'Twickenham', path: 'twickenham' },
    { name: 'Upminster', path: 'upminster' },
    { name: 'Uxbridge', path: 'uxbridge' },
    { name: 'Waltham Abbey', path: 'waltham-abbey' },
    { name: 'Walthamstow', path: 'walthamstow' },
    { name: 'Welling', path: 'welling' },
    { name: 'Wembley', path: 'wembley' },
    { name: 'West Drayton', path: 'west-drayton' },
    { name: 'Westminster', path: 'westminster' },
    { name: 'Whetstone', path: 'whetstone' },
    { name: 'Willesden', path: 'willesden' },
    { name: 'Wimbledon', path: 'wimbledon' },
    { name: 'Woodford Green', path: 'woodford-green' },
  ],
};

export const ALL_REGIONS: RegionDef[] = [
  northWest,
  northEast,
  yorkshire,
  eastMidlands,
  westMidlands,
  eastOfEngland,
  southEast,
  southWest,
  greaterLondon,
];

export function generateAllAreaConfigs(): AreaPageConfig[] {
  return ALL_REGIONS.flatMap((region) => generateConfigs(region));
}

export function getAllAreaPaths(): string[] {
  return ALL_REGIONS.flatMap((region) =>
    region.towns.map((town) => `/areas-we-cover/${town.path}`),
  );
}
